import { range } from "@components/ui-kit";
import { t } from "@lingui/macro";
import { appStore } from "@src/stores/AppStore";
import { now } from "@src/utils/dates";
import type { DateRange as Range } from "@src/utils/types";
import {
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  getDate,
  getDaysInMonth,
  set,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  subMonths,
  subQuarters,
  subWeeks,
  subYears,
} from "date-fns";
import { action, makeObservable, observable } from "mobx";

export type TRanges =
  | "all-time"
  | "this-week"
  | "last-week"
  | "this-semimonth"
  | "last-semimonth"
  | "this-month"
  | "last-month"
  | "this-quarter"
  | "last-quarter"
  | "this-year"
  | "last-year"
  | "custom";

export class DateRange {
  label: string;
  value: TRanges;
  @observable raw: Range | undefined;

  constructor(src: Omit<DateRange, "setRaw" | "copy">) {
    makeObservable(this);
    this.label = src.label;
    this.raw = src.raw;
    this.value = src.value;

    this.copy = this.copy.bind(this);
  }

  @action.bound
  setRaw(value: DateRange["raw"]) {
    this.raw = value;
  }

  copy() {
    return new DateRange({
      label: this.label,
      value: this.value,
      raw: this.raw,
    });
  }
}

export const getCommonRanges = (): DateRange[] => [
  new DateRange({
    label: t`All time`,
    value: "all-time",
    get raw() {
      return undefined;
    },
  }),
  new DateRange({
    label: t`This Week`,
    value: "this-week",
    get raw() {
      return {
        start: startOfWeek(now(), {
          weekStartsOn: appStore.workspaceStore.settings?.startOfWeekNumber,
        }),
        end: endOfWeek(now(), {
          weekStartsOn: appStore.workspaceStore.settings?.startOfWeekNumber,
        }),
      };
    },
  }),
  new DateRange({
    label: t`Last Week`,
    value: "last-week",
    get raw() {
      const d = subWeeks(now(), 1);
      return {
        start: startOfWeek(d, {
          weekStartsOn: appStore.workspaceStore.settings?.startOfWeekNumber,
        }),
        end: endOfWeek(d, {
          weekStartsOn: appStore.workspaceStore.settings?.startOfWeekNumber,
        }),
      };
    },
  }),
  new DateRange({
    label: t`This Semimonth`,
    value: "this-semimonth",
    get raw() {
      const days = getDaysInMonth(now());
      const half = Math.round(days / 2);
      const today = getDate(now());

      const start =
        today >= half ? set(now(), { date: half }) : set(now(), { date: 1 });
      const end =
        today >= half
          ? set(now(), { date: days })
          : set(now(), { date: half - 1 });

      return {
        start,
        end,
      };
    },
  }),
  new DateRange({
    label: t`Last Semimonth`,
    value: "last-semimonth",
    get raw() {
      const lastMonth = subMonths(now(), 1);
      const days = getDaysInMonth(lastMonth);
      const half = Math.round(days / 2);
      const today = getDate(now());

      const start =
        today >= half ? set(now(), { date: half }) : set(now(), { date: 1 });
      const end =
        today >= half
          ? set(now(), { date: days })
          : set(now(), { date: half - 1 });

      return {
        start,
        end,
      };
    },
  }),
  new DateRange({
    label: t`This Month`,
    value: "this-month",
    get raw() {
      return {
        start: startOfMonth(now()),
        end: endOfMonth(now()),
      };
    },
  }),
  new DateRange({
    label: t`Last Month`,
    value: "last-month",
    get raw() {
      const d = subMonths(now(), 1);
      return {
        start: startOfMonth(d),
        end: endOfMonth(d),
      };
    },
  }),
  new DateRange({
    label: t`This Quarter`,
    value: "this-quarter",
    get raw() {
      return {
        start: startOfQuarter(now()),
        end: endOfQuarter(now()),
      };
    },
  }),
  new DateRange({
    label: t`Last Quarter`,
    value: "last-quarter",
    get raw() {
      const d = subQuarters(now(), 1);
      return {
        start: startOfQuarter(d),
        end: endOfQuarter(d),
      };
    },
  }),
  new DateRange({
    label: t`This Year`,
    value: "this-year",
    get raw() {
      return {
        start: startOfYear(now()),
        end: endOfYear(now()),
      };
    },
  }),
  new DateRange({
    label: t`Last Year`,
    value: "last-year",
    get raw() {
      const d = subYears(now(), 1);
      return {
        start: startOfYear(d),
        end: endOfYear(d),
      };
    },
  }),
  new DateRange({
    label: t`Custom`,
    value: "custom",
    raw: range(),
  }),
];
